import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookmark } from '@fortawesome/free-solid-svg-icons';
import { customerService, type Customer, type CustomerLocation } from '@/services/customer.service';
import type { AddressSelection } from '@/services/equipment-intake.service';
import { Modal } from '@/components/shared/Modal';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/swal';
import { useHasPermission } from '@/hooks/use-permissions';

/**
 * Add or change an address, from inside the intake screens.
 *
 * The company's own registered address is the one edit with a blast radius wider
 * than this screen — rewriting it changes every future invoice for that customer.
 * So that case, and only that case, asks where to apply the change first, and
 * defaults to the option that leaves the customer untouched.
 *
 * "Use for this job only" is deliberately visible and disabled: the intake API
 * accepts a choice of which stored address to use, so an address that is never
 * stored has nothing to point at. Hiding it would just make operators ask.
 */

export type AddressFormMode =
  | { kind: 'create' }
  | { kind: 'edit-place'; location: CustomerLocation }
  | { kind: 'edit-company'; customer: Customer };

type CompanyEditScope = 'company' | 'new';

interface Fields {
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone: string;
}

const EMPTY: Fields = {
  name: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'India',
  phone: '',
};

function fieldsFor(mode: AddressFormMode): Fields {
  if (mode.kind === 'edit-place') {
    const l = mode.location;
    return {
      name: l.name,
      addressLine1: l.addressLine1 ?? l.address ?? '',
      addressLine2: l.addressLine2 ?? '',
      city: l.city ?? '',
      state: l.state ?? '',
      postalCode: l.postalCode ?? '',
      country: l.country ?? 'India',
      phone: l.phone ?? '',
    };
  }
  if (mode.kind === 'edit-company') {
    const c = mode.customer;
    return {
      name: c.name,
      addressLine1: c.addressLine1 ?? c.address ?? '',
      addressLine2: c.addressLine2 ?? '',
      city: c.city ?? '',
      state: c.state ?? '',
      postalCode: c.postalCode ?? '',
      country: c.country ?? 'India',
      phone: c.phone ?? '',
    };
  }
  return EMPTY;
}

function apiMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

export function AddressFormModal({
  open,
  customerId,
  mode,
  onClose,
  onSaved,
}: {
  open: boolean;
  customerId: string;
  mode: AddressFormMode;
  onClose: () => void;
  /** The new selection, so the caller can select it immediately. */
  onSaved: (selection: AddressSelection) => void;
}) {
  const queryClient = useQueryClient();
  const can = useHasPermission();
  const [f, setF] = useState<Fields>(EMPTY);
  const [scope, setScope] = useState<CompanyEditScope>('new');

  const isCompanyEdit = mode.kind === 'edit-company';
  // Rewriting the customer record is a customer edit, not an intake edit. A role
  // without the grant must not see a control that would 403 on save.
  const mayUpdateCompany = can('customer:update');

  useEffect(() => {
    if (open) {
      setF(fieldsFor(mode));
      setScope('new');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = <K extends keyof Fields>(k: K, v: Fields[K]) => setF((p) => ({ ...p, [k]: v }));

  const mutation = useMutation({
    mutationFn: async (): Promise<AddressSelection> => {
      const payload = {
        name: f.name.trim(),
        addressLine1: f.addressLine1.trim(),
        addressLine2: f.addressLine2.trim() || undefined,
        city: f.city.trim() || undefined,
        state: f.state.trim() || undefined,
        postalCode: f.postalCode.trim() || undefined,
        country: f.country.trim() || undefined,
        phone: f.phone.trim() || undefined,
      };

      if (mode.kind === 'edit-place') {
        const updated = await customerService.updateLocation(customerId, mode.location.id, payload);
        return { source: 'LOCATION', locationId: updated.id };
      }

      if (isCompanyEdit && scope === 'company') {
        await customerService.update(customerId, {
          addressLine1: payload.addressLine1,
          addressLine2: payload.addressLine2,
          city: payload.city,
          state: payload.state,
          postalCode: payload.postalCode,
          country: payload.country,
          phone: payload.phone,
        });
        return { source: 'CUSTOMER_DEFAULT' };
      }

      const created = await customerService.createLocation(customerId, payload);
      return { source: 'LOCATION', locationId: created.id };
    },
    onSuccess: (selection) => {
      void queryClient.invalidateQueries({ queryKey: ['customer-locations', customerId] });
      void queryClient.invalidateQueries({ queryKey: ['customer-resolve', customerId] });
      toast.success(mode.kind === 'create' ? 'Address added' : 'Address updated');
      onSaved(selection);
      onClose();
    },
    onError: (error: Error) => toast.error(apiMessage(error, 'Could not save the address')),
  });

  const title =
    mode.kind === 'create'
      ? 'Add New Address'
      : mode.kind === 'edit-place'
        ? `Edit ${mode.location.name}`
        : `Edit ${mode.customer.name}`;

  const canSubmit = f.name.trim().length > 0 && f.addressLine1.trim().length > 0;

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) mutation.mutate();
        }}
      >
        {isCompanyEdit && (
          <fieldset className="space-y-2 rounded-lg border border-amber-500/50 bg-amber-500/5 p-3">
            <legend className="px-1 text-sm font-semibold text-amber-700 dark:text-amber-500">
              Where do you want to apply this change?
            </legend>

            {mayUpdateCompany && (
              <label className="flex cursor-pointer items-start gap-2 rounded-md p-1 text-[13px]">
                <input
                  type="radio"
                  name="address-scope"
                  className="mt-1 accent-amber-600"
                  checked={scope === 'company'}
                  disabled={mutation.isPending}
                  onChange={() => setScope('company')}
                />
                <span>
                  <span className="font-semibold">Update the company address for future jobs</span>
                  <span className="block text-muted-foreground">
                    Changes it everywhere. Jobs already invoiced keep their address.
                  </span>
                </span>
              </label>
            )}

            <label className="flex cursor-pointer items-start gap-2 rounded-md p-1 text-[13px]">
              <input
                type="radio"
                name="address-scope"
                className="mt-1 accent-amber-600"
                checked={scope === 'new'}
                disabled={mutation.isPending}
                onChange={() => setScope('new')}
              />
              <span>
                <span className="font-semibold">Add it as a new address for this customer</span>
                <span className="block text-muted-foreground">
                  Leaves the company untouched. Picked for this job right away.
                </span>
              </span>
            </label>

            <label className="flex cursor-not-allowed items-start gap-2 rounded-md p-1 text-[13px] opacity-60">
              <input type="radio" name="address-scope" className="mt-1" disabled />
              <span>
                <span className="font-semibold">
                  Use for this job only
                  <span className="ml-1.5 rounded bg-muted px-1.5 py-0.5 text-[11px] font-semibold text-muted-foreground">
                    Coming soon
                  </span>
                </span>
              </span>
            </label>
          </fieldset>
        )}

        <FormField label="Location name" htmlFor="addr-name" required>
          <Input
            id="addr-name"
            value={f.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Chennai Service Center, Main Warehouse, Wind Farm Site 12…"
            disabled={mutation.isPending || (isCompanyEdit && scope === 'company')}
            required
          />
        </FormField>

        <FormField label="Address line 1" htmlFor="addr-l1" required>
          <Input
            id="addr-l1"
            value={f.addressLine1}
            onChange={(e) => set('addressLine1', e.target.value)}
            placeholder="Building, street"
            disabled={mutation.isPending}
            required
          />
        </FormField>

        <FormField label="Address line 2" htmlFor="addr-l2">
          <Input
            id="addr-l2"
            value={f.addressLine2}
            onChange={(e) => set('addressLine2', e.target.value)}
            placeholder="Area, landmark (optional)"
            disabled={mutation.isPending}
          />
        </FormField>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="City" htmlFor="addr-city">
            <Input
              id="addr-city"
              value={f.city}
              onChange={(e) => set('city', e.target.value)}
              disabled={mutation.isPending}
            />
          </FormField>
          <FormField label="State" htmlFor="addr-state">
            <Input
              id="addr-state"
              value={f.state}
              onChange={(e) => set('state', e.target.value)}
              disabled={mutation.isPending}
            />
          </FormField>
          <FormField label="PIN code" htmlFor="addr-pin">
            <Input
              id="addr-pin"
              value={f.postalCode}
              onChange={(e) => set('postalCode', e.target.value)}
              disabled={mutation.isPending}
            />
          </FormField>
          <FormField label="Country" htmlFor="addr-country">
            <Input
              id="addr-country"
              value={f.country}
              onChange={(e) => set('country', e.target.value)}
              disabled={mutation.isPending}
            />
          </FormField>
        </div>

        <FormField label="Phone" htmlFor="addr-phone">
          <Input
            id="addr-phone"
            value={f.phone}
            onChange={(e) => set('phone', e.target.value)}
            disabled={mutation.isPending}
          />
        </FormField>

        <label className="flex cursor-not-allowed items-start gap-2.5 rounded-lg border border-border bg-muted/60 p-3 opacity-60">
          <input type="checkbox" className="mt-1 h-4 w-4" disabled />
          <span>
            <span className="flex items-center gap-1.5 text-sm font-semibold">
              <FontAwesomeIcon icon={faBookmark} className="h-3 w-3" />
              Save for future use
              <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-semibold text-muted-foreground">
                Coming soon
              </span>
            </span>
            <span className="block text-[13px] text-muted-foreground">
              New addresses are kept for this customer for now.
            </span>
          </span>
        </label>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit || mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
